import React from 'react';
import {
  styled,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Box,
  Button,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  TablePagination,
  TextField,
  FormLabel,
  CircularProgress,
  Snackbar,
  AlertColor,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useTranslation } from 'react-i18next';
import TextInput from '../../form/TextInput';
import { Controller, useForm } from 'react-hook-form';
import validationUtils from '../../../services/validationUtils';
import EmojiTransportationIcon from '@mui/icons-material/EmojiTransportation';
import * as userSlice from '../../../data/user.slice';
import Toolbar from './Toolbar';
import { useNavigate } from 'react-router-dom';

const PREFIX = 'OnboardUser';

const classes = {
  container: `${PREFIX}-container`,
  form: `${PREFIX}-form`,
  card: `${PREFIX}-card`,
  cardContent: `${PREFIX}-cardContent`,
  button: `${PREFIX}-button`,
  hidden: `${PREFIX}-hidden`,
  provider: `${PREFIX}-provider`,
};

const StyledBox = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },

  [`& .${classes.form}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'row',
  },

  [`& .${classes.card}`]: {
    width: 500,
    margin: theme.spacing(1),
  },

  [`& .${classes.cardContent}`]: {
    display: 'flex',
    flexDirection: 'column',
  },

  [`& .${classes.button}`]: {
    margin: theme.spacing(1),
    marginBottom: theme.spacing(2),
    width: 100,
  },

  [`& .${classes.hidden}`]: {
    visibility: 'hidden',
  },

  [`& .${classes.provider}`]: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    marginLeft: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
  },
}));

export default function OnboardUser() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const requiredFieldMessage = t('common.requiredField');
  const { control, formState, handleSubmit, setValue, reset, watch } = useForm({
    defaultValues: {
      ProviderAssignmentMode: '1', // 1 = New, 2 = Existing
      ProviderID: null,
      ProviderName: '',
      NewProviderName: '',
      FirstName: '',
      LastName: '',
      Email: '',
    } as any,
  });

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const providerAssignmentMode = watch('ProviderAssignmentMode');

  const [snackbarMessage, setSnackbarMessage] = React.useState<string>();
  const [snackbarSeverity, setSnackbarSeverity] =
    React.useState<AlertColor>('error');
  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);

  const onboardingUser = useAppSelector(userSlice.selectOnboardingUser);
  const onboardingError = useAppSelector(userSlice.selectOnboardingUserError);
  const onboardingSuccess = useAppSelector(
    userSlice.selectOnboardingUserSuccess,
  );

  const searchFilter = useAppSelector(userSlice.selectProviderSearchFilter);

  const loadingProviders = useAppSelector(userSlice.selectLoadingProviderList);
  const providers = useAppSelector(userSlice.selectProviderList);
  React.useEffect(() => {
    dispatch(
      userSlice.fetchProviderListAsync(rowsPerPage, page, searchFilter, null),
    );
  }, [dispatch, page, rowsPerPage, searchFilter]);

  React.useEffect(() => {
    if (onboardingError) {
      setSnackbarMessage(onboardingError);
      setSnackbarSeverity('error');
    } else if (onboardingSuccess) {
      // The next two lines were removed since we pop up a dialog and it was considered redundant.
      // setSnackbarMessage(onboardingSuccess);
      // setSnackbarSeverity('success');
      setDialogOpen(true);
    }
    dispatch(userSlice.resetOnboardingFlags());
  }, [dispatch, onboardingError, onboardingSuccess]);

  const handleChangePage = (_event: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onSearchChange = (val: string) => {
    dispatch(userSlice.setProviderSearchFilter(val));
  };

  const selectProvider = (event: any) => {
    const id = parseInt(event.target.value, 10);
    const provider = providers.items.find(
      (p) => p.transportationProviderID === id,
    );
    setValue('ProviderID', id);
    setValue('ProviderName', provider?.transportationProviderName || '');
  };

  const onSnackbarClose = () => {
    setSnackbarMessage(undefined);
    dispatch(userSlice.resetOnboardingFlags());
  };

  const closeDialog = (yes: boolean) => {
    if (yes) {
      reset();
    } else {
      navigate('/');
    }
    setDialogOpen(false);
  };

  const saveUser = React.useCallback(
    (data: any) => {
      const name =
        data.ProviderAssignmentMode === '1' ? data.NewProviderName : undefined;
      const id =
        data.ProviderAssignmentMode === '2' ? data.ProviderID : undefined;
      dispatch(
        userSlice.onboardUserAsync(
          data.FirstName,
          data.LastName,
          data.Email,
          id,
          name,
        ),
      );
    },
    [dispatch],
  );

  return (
    <StyledBox className={classes.container}>
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={6000}
        onClose={onSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert variant="filled" severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Box className={classes.form}>
        <Card className={classes.card}>
          <CardHeader title={t('onboard.userInfo')} />
          <CardContent className={classes.cardContent}>
            <TextInput
              label={t('onboard.firstName')}
              name="FirstName"
              required={true}
              control={control}
              errors={formState.errors}
              rules={{
                required: requiredFieldMessage,
              }}
            />
            <TextInput
              label={t('onboard.lastName')}
              name="LastName"
              required={true}
              control={control}
              errors={formState.errors}
              rules={{
                required: requiredFieldMessage,
              }}
            />
            <TextInput
              label={t('onboard.email')}
              name="Email"
              required={true}
              control={control}
              errors={formState.errors}
              rules={{
                required: requiredFieldMessage,
                pattern: validationUtils.emailValidation(),
              }}
              inputProps={{ maxLength: 255 }}
            />
          </CardContent>
        </Card>
        <Card className={classes.card}>
          <CardHeader title={t('onboard.providerInfo')} />
          <CardContent className={classes.cardContent}>
            <Controller
              rules={{ required: true }}
              control={control}
              name="ProviderAssignmentMode"
              render={({ field }) => (
                <RadioGroup {...field}>
                  <FormControlLabel
                    key="1"
                    control={
                      <Radio value="1" color="primary" data-testid="radio1" />
                    }
                    label={t('onboard.newProvider')}
                  />
                  {providerAssignmentMode === '1' && (
                    <Box className={classes.provider}>
                      <TextInput
                        label={t('onboard.providerName')}
                        name="NewProviderName"
                        required={true}
                        control={control}
                        errors={formState.errors}
                        rules={{
                          required: requiredFieldMessage,
                        }}
                      />
                    </Box>
                  )}
                  <FormControlLabel
                    key="2"
                    control={
                      <Radio value="2" color="primary" data-testid="radio2" />
                    }
                    label={t('onboard.existingProvider')}
                  />
                  {providerAssignmentMode === '2' && (
                    <Box className={classes.provider}>
                      <FormControl>
                        <Controller
                          name="ProviderName"
                          control={control}
                          rules={{ required: requiredFieldMessage }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label={t('onboard.selectedProvider')}
                              required={true}
                              inputProps={{ readOnly: true }}
                              variant="filled"
                              helperText={
                                formState.errors ? (
                                  <>
                                    {formState.errors['ProviderName']?.message}
                                  </>
                                ) : undefined
                              }
                              error={
                                formState.errors
                                  ? !!formState.errors['ProviderName']
                                  : false
                              }></TextField>
                          )}
                        />
                        <TextInput
                          label={'providerid'}
                          name="ProviderID"
                          control={control}
                          required={true}
                          className={classes.hidden}
                        />
                        <FormLabel color="info">
                          {t('onboard.chooseProvider')}
                        </FormLabel>
                        <Toolbar
                          currentFilter={''}
                          searchIcon={<EmojiTransportationIcon />}
                          searchPlaceholder={t('onboard.searchProvider')}
                          currentSearchString={searchFilter}
                          onSearchChange={onSearchChange}
                        />
                        {loadingProviders && <CircularProgress />}
                        <RadioGroup onChange={selectProvider}>
                          {providers.items.map((p) => (
                            <FormControlLabel
                              key={p.transportationProviderID}
                              control={
                                <Radio
                                  value={p.transportationProviderID}
                                  color="primary"
                                />
                              }
                              label={p.transportationProviderName}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                      <TablePagination
                        component="div"
                        count={providers.itemCount}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPageOptions={[5, 10, 20, 50]}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                      />
                    </Box>
                  )}
                </RadioGroup>
              )}
            />
          </CardContent>
        </Card>
      </Box>
      <Button
        variant="contained"
        color="primary"
        disabled={onboardingUser}
        className={classes.button}
        onClick={handleSubmit(saveUser)}>
        {t('onboard.submit')}
      </Button>

      <Dialog open={dialogOpen} disableEscapeKeyDown={true}>
        <DialogTitle>{t('onboard.success')}</DialogTitle>
        <DialogContent>
          <DialogContentText color="textPrimary">
            {t('onboard.successMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="info" onClick={() => closeDialog(true)}>
            {t('onboard.createAnother')}
          </Button>
          <Button color="info" onClick={() => closeDialog(false)} autoFocus>
            {t('onboard.returnToRideCafe')}
          </Button>
        </DialogActions>
      </Dialog>
    </StyledBox>
  );
}
